import {
  createContext,
  lazy,
  Suspense,
  useContext,
  useEffect,
  useState,
} from "react";
import Modal from "react-modal";
import Spinner from "../atoms/Spinner";

import { getQumuSources } from "../../services/videoLookupService.js";
import AppSettings from "../../appsettings.js";
import "../../styles/scss/shareModal.scss";

// video.js plus its YouTube tech is ~210 kB gzipped and cannot render until the
// user opens a video. This provider is mounted by AppProviders, so a static
// import would pull the player into the entry chunk and charge every boot for
// it — including the logged-out boot, which only exists to discover there is no
// session and then redirects away.
const VideoJSComponent = lazy(
  () => import("../molecules/videoJSPlayer/VideoJSComponent.js"),
);

const VideoModalContext = createContext();
export const VideoModalProvider = ({ children }) => {
  const [videoId, setVideoId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [trackData, setTrackData] = useState({});
  const [loading, setLoading] = useState(true);
  const [fireOnClick, setFireOnClick] = useState(() => () => {});

  const openModal = (id, fireOnClick) => {
    setVideoId(id);
    setIsModalOpen(true);
    setFireOnClick(() => fireOnClick);
  };

  const handleAfterClose = () => {
    setVideoId(null);
    if (fireOnClick) {
      fireOnClick({
        label: "Video Modal Close",
        contentTitle: trackData.title,
      });
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  useEffect(() => {
    async function fetchSources() {
      if (videoId) {
        try {
          const qumuSources = await getQumuSources(videoId);
          setTrackData(qumuSources);
        } catch (error) {
          console.error("Error fetching sources", error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }
    fetchSources();
  }, [videoId]);

  return (
    <VideoModalContext.Provider value={{ videoId, openModal, closeModal }}>
      {children}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        onAfterClose={handleAfterClose}
        contentLabel="Video Modal"
        overlayClassName="share-modal-overlay share-modal-overlay--video"
        className="share-modal-content share-modal-content--video"
        closeTimeoutMS={300}
      >
        <h2 className="share-modal-title">{trackData.title}</h2>
        {loading ? (
          <Spinner />
        ) : (
          <div className="video-wrapper">
            <Suspense fallback={<Spinner />}>
              <VideoJSComponent trackData={trackData} />
            </Suspense>
          </div>
        )}
        <CloseButton closeModal={closeModal} />
      </Modal>
    </VideoModalContext.Provider>
  );
};

const CloseButton = ({ closeModal }) => (
  <button className="share-modal-close-button" onClick={closeModal}>
    <img
      src={AppSettings.AWSImgRepo.resources + `x_close_icon.svg`}
      alt="Close"
    />
  </button>
);

export const useVideoModalContext = () => useContext(VideoModalContext);
